import React, { useEffect, useState, useCallback } from 'react';
import { TabsContent } from '../../../components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../components/ui/tooltip';
import { Loader2, CheckCircle2, AlertTriangle, MinusCircle } from 'lucide-react';
import { toast } from 'sonner';
import { forestaOpsApi } from '../../../lib/api';
import { MonthPeriodHeader } from '../../../components/common/MonthPeriodHeader';

const ITEM_LABELS = {
  expensas: 'Expensas',
  electricidad: 'Electricidad',
  servicios_inquilino: 'Serv. inq.',
  tasas_municipales: 'T. mun.',
  habilitacion_comercial: 'Habilit.',
  seguro: 'Seguro',
  mantenimiento: 'Manten.',
  modificaciones_no_autorizadas: 'Sin mod.',
};

const ITEM_FULL = {
  expensas: 'Expensas pagadas',
  electricidad: 'Electricidad al día',
  servicios_inquilino: 'Servicios a nombre del inquilino',
  tasas_municipales: 'Tasas municipales al día',
  habilitacion_comercial: 'Habilitación comercial vigente (solo locales)',
  seguro: 'Seguro vigente',
  mantenimiento: 'Estado mantenimiento al día',
  modificaciones_no_autorizadas: 'Sin modificaciones no autorizadas',
};

// Grupos visuales: críticos a la izquierda, secundarios a la derecha.
const CRITICAL_KEYS = ['servicios_inquilino', 'expensas', 'electricidad', 'tasas_municipales'];
const SECONDARY_KEYS = ['habilitacion_comercial', 'seguro', 'mantenimiento', 'modificaciones_no_autorizadas'];
const ITEMS_SOLO_LOCAL = new Set(['habilitacion_comercial']);

const ESTADO_BADGE = {
  ok: { Icon: CheckCircle2, cls: 'bg-emerald-50 text-emerald-700 ring-emerald-100 hover:bg-emerald-100', label: 'OK' },
  falta: { Icon: AlertTriangle, cls: 'bg-orange-50 text-orange-700 ring-orange-100 hover:bg-orange-100', label: 'Falta' },
  observado: { Icon: AlertTriangle, cls: 'bg-amber-50 text-amber-700 ring-amber-100 hover:bg-amber-100', label: 'Obs' },
  no_aplica: { Icon: MinusCircle, cls: 'bg-slate-100 text-slate-500 ring-slate-200 hover:bg-slate-200', label: 'N/A' },
};

const GENERAL_BADGE = {
  ok: { cls: 'bg-emerald-50 text-emerald-700 ring-emerald-100', label: 'OK' },
  pendiente: { cls: 'bg-orange-50 text-orange-700 ring-orange-100', label: 'Pendiente' },
  observado: { cls: 'bg-amber-50 text-amber-700 ring-amber-100', label: 'Observado' },
};

const ORDER = ['falta', 'ok', 'observado', 'no_aplica'];

export const TabControlMensualForesta = ({ edificioId, periodo, setSelectedPeriodo, activeTab }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await forestaOpsApi.listControl(edificioId, periodo);
      setItems(r.data.items || []);
    } catch (e) { toast.error('Error al cargar control mensual'); }
    finally { setLoading(false); }
  }, [edificioId, periodo]);

  useEffect(() => {
    if (activeTab !== 'egresos') return;
    load();
  }, [activeTab, load]);

  const cycleItem = async (row, key) => {
    // Habilitación: bloquear edición si no es local
    const esLocal = (row.tipo || '').toLowerCase() === 'local';
    if (ITEMS_SOLO_LOCAL.has(key) && !esLocal) return;
    const cur = row.items[key]?.estado || 'falta';
    const next = ORDER[(ORDER.indexOf(cur) + 1) % ORDER.length];
    const newItems = {
      ...row.items,
      [key]: { ...(row.items[key] || {}), estado: next },
    };
    // Optimistic update
    setItems(prev => prev.map(it => it.unidad_id === row.unidad_id ? { ...it, items: newItems } : it));
    setSavingKey(`${row.unidad_id}-${key}`);
    try {
      const data = {
        items: newItems,
        observacion_general: row.observacion_general || '',
      };
      const r = await forestaOpsApi.upsertControl(edificioId, row.unidad_id, periodo, data);
      // Update estado_general from server
      setItems(prev => prev.map(it => it.unidad_id === row.unidad_id ? { ...it, items: newItems, estado_general: r.data.estado_general } : it));
    } catch (e) {
      toast.error('Error al guardar');
      load();
    } finally { setSavingKey(null); }
  };

  const renderItemCell = (it, k, isFirstSecondary) => {
    const v = it.items[k] || { estado: 'falta' };
    const esLocal = (it.tipo || '').toLowerCase() === 'local';
    const bloqueado = ITEMS_SOLO_LOCAL.has(k) && !esLocal;
    const b = ESTADO_BADGE[v.estado] || ESTADO_BADGE.falta;
    const isSaving = savingKey === `${it.unidad_id}-${k}`;
    const sepCls = isFirstSecondary ? 'border-l border-slate-300' : '';

    if (bloqueado) {
      return (
        <td key={k} className={`py-2.5 px-1.5 text-center ${sepCls}`}>
          <span
            className="inline-flex items-center justify-center text-slate-400 text-base font-medium"
            title={`${ITEM_FULL[k]} — no aplica a oficinas`}
            data-testid={`ctrl-${it.unidad_id}-${k}-na`}
          >
            —
          </span>
        </td>
      );
    }

    return (
      <td key={k} className={`py-2.5 px-1.5 text-center ${sepCls}`}>
        <TooltipProvider delayDuration={250}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => cycleItem(it, k)}
                disabled={isSaving}
                data-testid={`ctrl-${it.unidad_id}-${k}`}
                className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium ring-1 transition-colors ${b.cls} disabled:opacity-50`}
              >
                {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <b.Icon className="w-3 h-3" />}
                {b.label}
              </button>
            </TooltipTrigger>
            <TooltipContent side="top">
              <div className="text-xs">
                <div className="font-semibold">{ITEM_FULL[k]}</div>
                <div className="text-slate-300">Click para cambiar estado</div>
                {v.observacion && <div className="text-amber-300 mt-1">{v.observacion}</div>}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </td>
    );
  };

  return (
    <TabsContent value="egresos" className="mt-0">
      <div className="space-y-2 flex-1 min-h-0 flex flex-col">
        {setSelectedPeriodo && (
          <MonthPeriodHeader
            value={periodo}
            onChange={setSelectedPeriodo}
            testid="foresta-header-control"
            right={
              <span className="text-[11px] text-slate-500">
                Click sobre cada ítem para ciclar estado: <b>Falta → OK → Observado → No aplica</b>
              </span>
            }
          />
        )}
        <div className="bg-white rounded-lg border border-slate-200 flex-1 min-h-0 flex flex-col overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : (
            <div className="overflow-auto flex-1 min-h-0">
              <table className="w-full text-sm min-h-full">
                <thead className="bg-slate-50">
                  <tr className="border-b border-slate-200">
                    <th className="sticky left-0 bg-slate-50 z-10 text-left text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-3">Unidad</th>
                    <th className="text-left text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-2">Estado</th>
                    {CRITICAL_KEYS.map(k => (
                      <th
                        key={k}
                        className="text-center text-[11px] font-bold uppercase text-slate-700 bg-slate-100/70 py-2.5 px-2"
                        title={ITEM_FULL[k]}
                      >
                        {ITEM_LABELS[k]}
                      </th>
                    ))}
                    {SECONDARY_KEYS.map((k, idx) => (
                      <th
                        key={k}
                        className={`text-center text-[10px] font-medium uppercase text-slate-500 py-2.5 px-1.5 ${idx === 0 ? 'border-l border-slate-300' : ''}`}
                        title={ITEM_FULL[k]}
                      >
                        {ITEM_LABELS[k]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map(it => {
                    const gen = GENERAL_BADGE[it.estado_general] || GENERAL_BADGE.pendiente;
                    return (
                      <tr key={it.unidad_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`ctrl-row-${it.unidad_id}`}>
                        <td className="sticky left-0 bg-white py-2.5 px-3 font-medium text-slate-900 whitespace-nowrap">
                          {it.unidad_nombre}
                        </td>
                        <td className="py-2.5 px-2">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 ${gen.cls}`} data-testid={`ctrl-general-${it.unidad_id}`}>
                            {gen.label}
                          </span>
                        </td>
                        {CRITICAL_KEYS.map(k => renderItemCell(it, k, false))}
                        {SECONDARY_KEYS.map((k, idx) => renderItemCell(it, k, idx === 0))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </TabsContent>
  );
};

export default TabControlMensualForesta;
